import React, { useState, useEffect, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendGasFreeCheckEmail from './gasFreeCheckEmailService';
import { lookupVesselBySerial } from './formTraceabilityService';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import { getLondonDateKey } from '../utils/londonDate';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const GasFreeCheckForm = ({ onBack, bumpGateStatus }) => {
  const [formData, setFormData] = useState({
    userName: '',
    date: getLondonDateKey(),
    serialNumber: '',
    waterCapacity: '',
    owner: '',
    lel: '',
  });
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [highLelModalOpen, setHighLelModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const bumpTestUnlocked = Boolean(bumpGateStatus?.gasFreeCheckUnlocked);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData((prev) => (prev.userName ? prev : { ...prev, userName: name }));
  }, []);

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.serialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setFormData((prev) => ({ ...prev, waterCapacity: '', owner: '' }));
        setSerialModalOpen(true);
        return;
      }

      const vessel = result.vessel || {};
      setFormData((prev) => ({
        ...prev,
        waterCapacity: vessel.vesselSize !== null && vessel.vesselSize !== undefined ? String(vessel.vesselSize) : '',
        owner: vessel.owner || '',
      }));
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.serialNumber, serialLookupState.lastLookup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setSubmitMessage('');

    if (!bumpTestUnlocked) {
      setSubmitMessage('Complete the Bump Test before submitting a Gas Free Check.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.userName.trim()) {
      setSubmitMessage('Unable to determine your name from login credentials. Please log in again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.serialNumber.trim() || !formData.waterCapacity || !formData.owner) {
      setSubmitMessage('Enter a valid serial number found in the Vessel Inventory before submitting.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const lelValue = parseFloat(formData.lel);
    if (!Number.isNaN(lelValue) && lelValue >= 1) {
      setHighLelModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await sendGasFreeCheckEmail(formData);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage(error.message || 'Error submitting form. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!bumpTestUnlocked) {
    return (
      <PVSPageLayout className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30 p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-3">Gas Free Check</h1>
            <p className="text-pvs-text-muted mb-2">
              Gas Free Check is locked until today&apos;s Bump Test has been completed.
            </p>
            <p className="text-sm text-amber-300 mb-6">
              Complete the Bump Test form first, then return here.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md text-sm font-medium text-pvs-accent hover:bg-pvs-accent/10"
            >
              <Home className="h-4 w-4" />
              Back to Forms
            </button>
          </div>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Gas Free Check</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-GFC-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Vessel gas free (LEL) verification</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-5 rounded-lg border-2 shadow-md bg-red-900/30 text-red-400 border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Name</label>
                <input
                  type="text"
                  value={formData.userName}
                  readOnly
                  className="block w-full pvs-input opacity-80 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date</label>
                <input
                  type="text"
                  value={formatDisplayDate(formData.date)}
                  readOnly
                  className="block w-full pvs-input opacity-80 cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">Serial Number *</label>
              <input
                type="text"
                required
                value={formData.serialNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({ ...prev, serialNumber: value }));
                  if (serialLookupState.lastLookup) {
                    setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
                    setFormData((prev) => ({ ...prev, serialNumber: value, waterCapacity: '', owner: '' }));
                  }
                }}
                onBlur={handleSerialLookup}
                className="block w-full pvs-input"
              />
              {serialLookupState.loading && (
                <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
              )}
              {!serialLookupState.loading && serialLookupState.notFound && (
                <p className="mt-1 text-xs text-red-400">Serial number not found in Vessel Inventory.</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Water Capacity</label>
                <input
                  type="text"
                  value={formData.waterCapacity}
                  readOnly
                  placeholder="Auto-filled from serial number"
                  className="block w-full pvs-input opacity-80 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Owner</label>
                <input
                  type="text"
                  value={formData.owner}
                  readOnly
                  placeholder="Auto-filled from serial number"
                  className="block w-full pvs-input opacity-80 cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">LEL %</label>
              <input
                type="number"
                required
                min="0"
                step="0.1"
                value={formData.lel}
                onChange={(e) => setFormData((prev) => ({ ...prev, lel: e.target.value }))}
                className="block w-full pvs-input"
              />
            </div>

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                <Home className="h-4 w-4" />
                Back to Forms
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Gas Free Check'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <SerialValidationModal isOpen={serialModalOpen} onClose={() => setSerialModalOpen(false)} />

      {highLelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-70" onClick={() => setHighLelModalOpen(false)} />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full p-0 overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">Gas Contents Too High</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-white text-base font-semibold leading-relaxed">
                Gas contents are still too high. Please leave the vessel in a safe area to continue to vent and check again in 24 hours.
              </p>
            </div>
            <div className="px-6 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => setHighLelModalOpen(false)}
                autoFocus
                className="px-6 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg shadow hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <FormSuccessModal
        isOpen={showSuccessModal}
        formName="Gas Free Check"
        onClose={() => {
          setShowSuccessModal(false);
          onBack?.();
        }}
      />
    </PVSPageLayout>
  );
};

export default GasFreeCheckForm;
